import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import {
  getTasks,
  getStats,
  getWeeks,
  getUsers,
  getCategories,
} from './api/monday-meeting.api';
import type {
  MondayMeetingTask,
  MondayMeetingCategory,
  MondayMeetingStats,
  MondayMeetingFilters,
  WeekOption,
  User,
  TaskStatus,
} from './types/monday-meeting.types';
import { TaskModal } from './components/TaskModal';
import { TaskDetailModal } from './components/TaskDetailModal';
import { CategoryManager } from './components/CategoryManager';
import './MondayMeetingTab.css';

const STATUS_LABELS: Record<TaskStatus, string> = {
  pending: 'En attente',
  in_progress: 'En cours',
  completed: 'Terminé',
  cancelled: 'Annulé',
};

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: '#6b7280',
  medium: '#f59e0b',
  high: '#ef4444',
};

// Sortable columns type
type SortColumn = 'task_number' | 'title' | 'category' | 'assignees' | 'created_week' | 'due_week' | 'status' | 'priority';
type SortOrder = 'asc' | 'desc';

// Status order for sorting
const STATUS_ORDER: Record<TaskStatus, number> = {
  pending: 1,
  in_progress: 2,
  completed: 3,
  cancelled: 4,
};

// Priority order for sorting
const PRIORITY_ORDER: Record<string, number> = {
  high: 1,
  medium: 2,
  low: 3,
};

export function MondayMeetingTab() {
  // Data state
  const [tasks, setTasks] = useState<MondayMeetingTask[]>([]);
  const [stats, setStats] = useState<MondayMeetingStats | null>(null);
  const [weeks, setWeeks] = useState<WeekOption[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [categories, setCategories] = useState<MondayMeetingCategory[]>([]);
  const [currentWeek, setCurrentWeek] = useState<{ year: number; week: number } | null>(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0,
  });

  // Filters
  const [filters, setFilters] = useState<MondayMeetingFilters>({
    page: 1,
    limit: 50,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [weekFilter, setWeekFilter] = useState<{ year?: number; week?: number }>({});
  const [meetingWeekFilter, setMeetingWeekFilter] = useState<{ year?: number; week?: number }>({});

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<MondayMeetingTask | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Category manager
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  // Refresh trigger
  const [refreshKey, setRefreshKey] = useState(0);

  // Sorting
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Load initial data
  useEffect(() => {
    loadWeeks();
    loadUsers();
    loadStats();
    loadCategories();
  }, []);

  // Load tasks when filters change
  useEffect(() => {
    loadTasks();
  }, [filters, refreshKey]);

  // Update filters when search/status/week/category changes
  useEffect(() => {
    const debounce = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        category_id: categoryFilter || undefined,
        year: weekFilter.year,
        week: weekFilter.week,
        created_year: meetingWeekFilter.year,
        created_week: meetingWeekFilter.week,
        page: 1,
      }));
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, statusFilter, categoryFilter, weekFilter, meetingWeekFilter]);

  const loadWeeks = async () => {
    try {
      const response = await getWeeks();
      if (response.success) {
        setWeeks(response.data);
        setCurrentWeek(response.current);
      }
    } catch (error) {
      console.error('Error loading weeks:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await getUsers();
      if (response.success) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await getCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const response = await getTasks(filters);
      if (response.success) {
        setTasks(response.data);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
    loadStats();
  }, []);

  const handleTaskClick = (task: MondayMeetingTask) => {
    setSelectedTask(task);
    setIsDetailModalOpen(true);
  };

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    handleRefresh();
  };

  const handleUpdateSuccess = () => {
    handleRefresh();
  };

  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
    setSelectedTask(null);
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const clearWeekFilter = () => {
    setWeekFilter({});
  };

  const clearMeetingWeekFilter = () => {
    setMeetingWeekFilter({});
  };

  // Handle column sort click
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  // Sorted tasks
  const sortedTasks = useMemo(() => {
    if (!sortColumn) return tasks;

    return [...tasks].sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'task_number':
          comparison = a.task_number - b.task_number;
          break;
        case 'title':
          comparison = a.title.localeCompare(b.title, 'fr');
          break;
        case 'category': {
          const aCat = a.category_name || 'zzz';
          const bCat = b.category_name || 'zzz';
          comparison = aCat.localeCompare(bCat, 'fr');
          break;
        }
        case 'assignees': {
          const aName = a.assignees.length > 0 ? a.assignees[0].user_name : 'zzz';
          const bName = b.assignees.length > 0 ? b.assignees[0].user_name : 'zzz';
          comparison = aName.localeCompare(bName, 'fr');
          break;
        }
        case 'created_week':
          // Sort by year first, then week
          comparison = (a.created_year * 100 + a.created_week) - (b.created_year * 100 + b.created_week);
          break;
        case 'due_week':
          // Sort by year first, then week (late tasks have negative weeks_until_due)
          comparison = (a.due_year * 100 + a.due_week) - (b.due_year * 100 + b.due_week);
          break;
        case 'status':
          comparison = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
          break;
        case 'priority':
          comparison = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [tasks, sortColumn, sortOrder]);

  // Render sort icon
  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <ChevronUpDownIcon className="monday-meeting-sort-icon inactive" />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUpIcon className="monday-meeting-sort-icon active" />
    ) : (
      <ChevronDownIcon className="monday-meeting-sort-icon active" />
    );
  };

  return (
    <div className="monday-meeting-container">
      {/* Hero Header */}
      <div className="monday-meeting-hero">
        <div className="monday-meeting-hero-content">
          <h1>Réunion du lundi</h1>
          <p>Gestion des tâches discutées lors des réunions du lundi</p>
        </div>
        <div className="monday-meeting-hero-decoration">
          <div className="monday-meeting-hero-circle monday-meeting-hero-circle-1"></div>
          <div className="monday-meeting-hero-circle monday-meeting-hero-circle-2"></div>
          <div className="monday-meeting-hero-circle monday-meeting-hero-circle-3"></div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="monday-meeting-stats">
          <div className="monday-meeting-stat-card">
            <div className="monday-meeting-stat-icon total">
              <CalendarDaysIcon className="w-5 h-5" />
            </div>
            <div className="monday-meeting-stat-info">
              <span className="monday-meeting-stat-value">{stats.total_count}</span>
              <span className="monday-meeting-stat-label">Total</span>
            </div>
          </div>

          <div
            className={`monday-meeting-stat-card clickable ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'pending' ? '' : 'pending')}
          >
            <div className="monday-meeting-stat-icon pending">
              <ClockIcon className="w-5 h-5" />
            </div>
            <div className="monday-meeting-stat-info">
              <span className="monday-meeting-stat-value">{stats.pending_count}</span>
              <span className="monday-meeting-stat-label">En attente</span>
            </div>
          </div>

          <div
            className={`monday-meeting-stat-card clickable ${statusFilter === 'in_progress' ? 'active' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'in_progress' ? '' : 'in_progress')}
          >
            <div className="monday-meeting-stat-icon in-progress">
              <ChevronUpIcon className="w-5 h-5" />
            </div>
            <div className="monday-meeting-stat-info">
              <span className="monday-meeting-stat-value">{stats.in_progress_count}</span>
              <span className="monday-meeting-stat-label">En cours</span>
            </div>
          </div>

          <div
            className={`monday-meeting-stat-card clickable ${statusFilter === 'completed' ? 'active' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'completed' ? '' : 'completed')}
          >
            <div className="monday-meeting-stat-icon completed">
              <CheckCircleIcon className="w-5 h-5" />
            </div>
            <div className="monday-meeting-stat-info">
              <span className="monday-meeting-stat-value">{stats.completed_count}</span>
              <span className="monday-meeting-stat-label">Terminé</span>
            </div>
          </div>

          <div className="monday-meeting-stat-card late">
            <div className="monday-meeting-stat-icon late">
              <ExclamationTriangleIcon className="w-5 h-5" />
            </div>
            <div className="monday-meeting-stat-info">
              <span className="monday-meeting-stat-value">{stats.late_count}</span>
              <span className="monday-meeting-stat-label">En retard</span>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="monday-meeting-controls">
        <div className="monday-meeting-controls-left">
          {/* Search */}
          <div className="monday-meeting-search">
            <MagnifyingGlassIcon className="monday-meeting-search-icon" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="monday-meeting-search-input"
            />
            {searchTerm && (
              <button className="monday-meeting-search-clear" onClick={clearSearch}>
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Meeting Week Filter (when task was created/discussed) */}
          <div className="monday-meeting-week-filter">
            <select
              value={meetingWeekFilter.year && meetingWeekFilter.week ? `${meetingWeekFilter.year}-${meetingWeekFilter.week}` : ''}
              onChange={(e) => {
                if (e.target.value) {
                  const [year, week] = e.target.value.split('-').map(Number);
                  setMeetingWeekFilter({ year, week });
                } else {
                  clearMeetingWeekFilter();
                }
              }}
              className="monday-meeting-select"
            >
              <option value="">Toutes les réunions</option>
              {weeks.map((w) => (
                <option key={`meeting-${w.year}-${w.week}`} value={`${w.year}-${w.week}`}>
                  {w.label}
                  {currentWeek && w.year === currentWeek.year && w.week === currentWeek.week
                    ? ' (actuelle)'
                    : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Due Week Filter */}
          <div className="monday-meeting-week-filter">
            <select
              value={weekFilter.year && weekFilter.week ? `${weekFilter.year}-${weekFilter.week}` : ''}
              onChange={(e) => {
                if (e.target.value) {
                  const [year, week] = e.target.value.split('-').map(Number);
                  setWeekFilter({ year, week });
                } else {
                  clearWeekFilter();
                }
              }}
              className="monday-meeting-select"
            >
              <option value="">Toutes les échéances</option>
              {weeks.map((w) => (
                <option key={`due-${w.year}-${w.week}`} value={`${w.year}-${w.week}`}>
                  {w.label}
                  {currentWeek && w.year === currentWeek.year && w.week === currentWeek.week
                    ? ' (actuelle)'
                    : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Category (Sujet) Filter */}
          <div className="monday-meeting-category-filter">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="monday-meeting-select"
            >
              <option value="">Tous les sujets</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            <button
              className="monday-meeting-category-settings-btn"
              onClick={() => setShowCategoryManager(true)}
              title="Gérer les sujets"
            >
              <Cog6ToothIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="monday-meeting-controls-right">
          {/* Status Filter Buttons */}
          <div className="monday-meeting-status-filters">
            <button
              className={`monday-meeting-status-btn ${statusFilter === '' ? 'active' : ''}`}
              onClick={() => setStatusFilter('')}
            >
              Tous
            </button>
            <button
              className={`monday-meeting-status-btn ${statusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'pending' ? '' : 'pending')}
            >
              En attente
            </button>
            <button
              className={`monday-meeting-status-btn ${statusFilter === 'in_progress' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'in_progress' ? '' : 'in_progress')}
            >
              En cours
            </button>
            <button
              className={`monday-meeting-status-btn ${statusFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'completed' ? '' : 'completed')}
            >
              Terminé
            </button>
          </div>

          {/* Create Button */}
          <button className="monday-meeting-create-btn" onClick={() => setIsCreateModalOpen(true)}>
            <PlusIcon className="w-5 h-5" />
            <span>Nouvelle tâche</span>
          </button>
        </div>
      </div>

      {/* Task List */}
      <div className="monday-meeting-list">
        {loading ? (
          <div className="monday-meeting-loading">
            <div className="monday-meeting-spinner" />
            <p>Chargement...</p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="monday-meeting-empty">
            <CalendarDaysIcon className="monday-meeting-empty-icon" />
            <h3>Aucune tâche trouvée</h3>
            <p>Créez une nouvelle tâche pour commencer</p>
            <button
              className="monday-meeting-create-btn"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <PlusIcon className="w-5 h-5" />
              <span>Créer une tâche</span>
            </button>
          </div>
        ) : (
          <>
            <div className="monday-meeting-table">
              <div className="monday-meeting-table-header">
                <div
                  className="monday-meeting-col-number sortable"
                  onClick={() => handleSort('task_number')}
                >
                  # {renderSortIcon('task_number')}
                </div>
                <div
                  className="monday-meeting-col-title sortable"
                  onClick={() => handleSort('title')}
                >
                  Titre {renderSortIcon('title')}
                </div>
                <div
                  className="monday-meeting-col-category sortable"
                  onClick={() => handleSort('category')}
                >
                  Sujet {renderSortIcon('category')}
                </div>
                <div
                  className="monday-meeting-col-assignees sortable"
                  onClick={() => handleSort('assignees')}
                >
                  Assignés {renderSortIcon('assignees')}
                </div>
                <div
                  className="monday-meeting-col-meeting sortable"
                  onClick={() => handleSort('created_week')}
                >
                  Réunion {renderSortIcon('created_week')}
                </div>
                <div
                  className="monday-meeting-col-due sortable"
                  onClick={() => handleSort('due_week')}
                >
                  Échéance {renderSortIcon('due_week')}
                </div>
                <div
                  className="monday-meeting-col-status sortable"
                  onClick={() => handleSort('status')}
                >
                  Statut {renderSortIcon('status')}
                </div>
                <div
                  className="monday-meeting-col-priority sortable"
                  onClick={() => handleSort('priority')}
                >
                  Priorité {renderSortIcon('priority')}
                </div>
              </div>

              {sortedTasks.map((task) => (
                <div
                  key={task.id}
                  className={`monday-meeting-table-row ${task.is_late ? 'late' : ''}`}
                  onClick={() => handleTaskClick(task)}
                >
                  <div className="monday-meeting-col-number">
                    <span className="monday-meeting-task-number">#{task.task_number}</span>
                  </div>
                  <div className="monday-meeting-col-title">
                    <span className="monday-meeting-task-title">{task.title}</span>
                    {task.description && (
                      <span className="monday-meeting-task-desc">
                        {task.description.substring(0, 60)}
                        {task.description.length > 60 ? '...' : ''}
                      </span>
                    )}
                  </div>
                  <div className="monday-meeting-col-category">
                    {task.category_name ? (
                      <span className="monday-meeting-badge category">{task.category_name}</span>
                    ) : (
                      <span className="monday-meeting-no-category">—</span>
                    )}
                  </div>
                  <div className="monday-meeting-col-assignees">
                    {task.assignees.length === 0 ? (
                      <span className="monday-meeting-no-assignee">Non assigné</span>
                    ) : (
                      <div className="monday-meeting-assignee-list">
                        {task.assignees.map((a) => (
                          <span key={a.user_id} className="monday-meeting-assignee-avatar" title={a.user_name}>
                            {a.user_name.split(' ').map((n) => n[0]).join('').substring(0, 2)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="monday-meeting-col-meeting">
                    <span className="monday-meeting-badge meeting">S{task.created_week}</span>
                  </div>
                  <div className="monday-meeting-col-due">
                    {task.is_late ? (
                      <span className="monday-meeting-badge late">
                        {task.weeks_until_due} sem.
                      </span>
                    ) : task.weeks_until_due === 0 ? (
                      <span className="monday-meeting-badge current">
                        Cette sem.
                      </span>
                    ) : (
                      <span className="monday-meeting-badge future">
                        +{task.weeks_until_due} sem.
                      </span>
                    )}
                  </div>
                  <div className="monday-meeting-col-status">
                    <span className={`monday-meeting-status ${task.status}`}>
                      {STATUS_LABELS[task.status]}
                    </span>
                  </div>
                  <div className="monday-meeting-col-priority">
                    <span
                      className="monday-meeting-priority"
                      style={{ color: PRIORITY_COLORS[task.priority] }}
                    >
                      {PRIORITY_LABELS[task.priority]}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="monday-meeting-pagination">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  className="monday-meeting-page-btn"
                >
                  Précédent
                </button>
                <span className="monday-meeting-page-info">
                  Page {pagination.page} sur {pagination.totalPages}
                </span>
                <button
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => handlePageChange(pagination.page + 1)}
                  className="monday-meeting-page-btn"
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <TaskModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
          users={users}
          categories={categories}
          currentWeek={currentWeek}
        />
      )}

      {/* Detail Modal */}
      {isDetailModalOpen && selectedTask && (
        <TaskDetailModal
          isOpen={isDetailModalOpen}
          onClose={handleCloseDetail}
          task={selectedTask}
          onUpdate={handleUpdateSuccess}
          users={users}
          categories={categories}
        />
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <CategoryManager
          categories={categories}
          onClose={() => setShowCategoryManager(false)}
          onUpdate={loadCategories}
        />
      )}
    </div>
  );
}

export default MondayMeetingTab;
